import clsx from 'clsx';
import React, { useCallback, useRef, useState } from 'react';
import { SCHEMA_EXPLORER_ACTIVATION_UI_OPTIONS, DUPLICATE_COLUMN_ERROR_MESSAGE } from '../../constants/constants';
import {
  TRACKER_ACTIVATION_TEST_AND_CONTINUE_CLICK,
  TRACKER_ACTIVATION_TEST_AND_CONTINUE_SUCCESS
} from '../../tracking';
import { Target } from '../../../activate-target/models/target';
import { TargetTypesEnum } from '../../../activate-target/models/target-type';
import {
  HdCoordinatedHeight,
  HdCoordinatedHeightAnchor,
  useCoordinatedHeightElement
} from '../../../../layout/components/coordinated-height/HdCoordinatedHeight';
import { HdCoordinatedScroll, HdCoordinatedScrollAnchor } from '../../../../a11y/scroll/components/coordinated-scroll/HdCoordinatedScroll';
import { HdButton } from '../../../../components';
import { WizardStepBaseProps } from '../../../../wizard/interfaces/wizardStep';
import { useWizardStepData } from '../../../../wizard/hooks/useWizardStepData';
import {
  QueryExecutionResponse,
  QueryExecutionStatusEnum,
  QueryRunTypeEnum
} from '../../../../warehouse-management/workbench/interfaces/workbench';
import { useDataSource } from '../../../../warehouse-management/workbench/hooks/useDataSource';
import { checkResponseForDuplicateColumns } from '../../../../warehouse-management/workbench/utils/workbench';
import { Workbench } from '../../../../warehouse-management/workbench/components/workbench/Workbench';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import { TargetObjectExplorer } from '../../../activate-target/TargetObjectExplorer';
import TransformModelAPI from '../../../transform-model/TransformModelAPI';
import { useTrackActivationQueryExecutionStatus } from '../../useTrackActivationQueryExecutionStatus';
import styles from './styles.module.scss';

export function ActivationSourceSettings({
  initialData,
  controller,
  completeStep,
  onDataChange
}: WizardStepBaseProps) {
  const [ data, setData ] = useWizardStepData(initialData, onDataChange);

  const setDataAndCompleteStep = (d) => {
    setData({
      ...data,
      ...d
    });

    completeStep();
  };

  return (
    <ActivationSourceSettingsInner
      controller={controller}
      data={data}
      onStepComplete={setDataAndCompleteStep}
    />
  );
}

export function ActivationSourceSettingsInner({
  controller,
  data,
  onStepComplete
}) {
  const [ coordinatedScroll, setCoordinatedScroll ] = useState(null);
  const coordinatedScrollAnchorRef = useRef(null);
  const [ currentTargetObject, setCurrentTargetObject ] = useState(data.selectedTargetObject);

  const [ selectedQuery, setSelectedQuery ] = useState(data.selectedQuery || '');
  const [ runQueryType, setRunQueryType ] = useState<QueryRunTypeEnum>(null);
  const [ queryExecutionResponse, setQueryExecutionResponse ] = useState<QueryExecutionResponse>(
    data.queryOutputData ? {
      data: data.queryOutputData,
      status: QueryExecutionStatusEnum.SUCCESS
    } : null
  );

  const [ objectExplorerHeightElem, setObjectExplorerHeightElem ] = useCoordinatedHeightElement();

  const dataIdGenerator = getDataIdGenerator('activation-source-settings');

  const target: Target = controller.data.target;
  const destination = controller.data.destination;

  const {
    dataSource,
    dataSourceUIState,
    onSchemaDelete,
    getDataSource
  } = useDataSource({ dataSourceId: destination.id });

  const { eventTrack } = useAnalyticsTracker();
  const { trackQueryExecutionStatus } = useTrackActivationQueryExecutionStatus();

  const onQueryExecutionResponseChange = (res: QueryExecutionResponse) => {
    if (res.status === QueryExecutionStatusEnum.SUCCESS) {
      if (runQueryType === QueryRunTypeEnum.RUN_CONTINUE && !checkResponseForDuplicateColumns(res)) {
        onTestContinueSuccess(res);
      }
    }

    trackQueryExecutionStatus(res, null);
    setQueryExecutionResponse(res);
  };

  const onTestContinueClick = () => {
    eventTrack({
      action: TRACKER_ACTIVATION_TEST_AND_CONTINUE_CLICK,
      properties: {
        warehouseType: destination.dest_type,
        targetType: target.targetType,
        query: selectedQuery
      }
    });
  };

  const onTestContinueSuccess = (_queryExecutionResponse) => {
    eventTrack({
      action: TRACKER_ACTIVATION_TEST_AND_CONTINUE_SUCCESS,
      properties: {
        warehouseType: destination.dest_type,
        targetType: target.targetType,
        query: selectedQuery
      }
    });

    onStepComplete({
      selectedQuery,
      queryOutputData: _queryExecutionResponse?.data,
      selectedTargetObject: currentTargetObject
    });
  };

  const onRunQueryClick = () => {
    setRunQueryType(QueryRunTypeEnum.RUN);
  };

  const QueryEditorFooterActions = useCallback(({ runningQuery, runQuery, stopRunningQuery }) => {
    const onTestAndMapFieldsClick = () => {
      setRunQueryType(QueryRunTypeEnum.RUN_CONTINUE);

      onTestContinueClick();

      runningQuery ? stopRunningQuery() : runQuery();
    };

    return (
      <HdButton
        className='ml-4'
        dataId={dataIdGenerator('test-map-fields')}
        onClick={onTestAndMapFieldsClick}
        icon={runQueryType === QueryRunTypeEnum.RUN_CONTINUE && runningQuery ? 'testing' : 'enter'}
        disabled={(runningQuery && runQueryType !== QueryRunTypeEnum.RUN_CONTINUE) || !selectedQuery}
      >
        {runQueryType === QueryRunTypeEnum.RUN_CONTINUE && runningQuery ? 'Stop Validating Query' : 'Test & Map Fields'}
      </HdButton>
    );
  }, [ runQueryType, onTestContinueClick ]);

  const canShowObjectExplorer = target.targetType !== TargetTypesEnum.GOOGLE_SHEETS;

  return (
    <div className={styles.activationSourceSettings}>
      <HdCoordinatedScroll elRef={coordinatedScroll}>
        <HdCoordinatedHeightAnchor
          Component='div'
          id={dataIdGenerator('')}
          className='scrollable-content bg-surface surface-on-surface'
          heightOffset={40}
          ref={(el) => {
            setCoordinatedScroll(el);
          }}
        >
          <div className='body-container'>
            <div className='header-container'>
              <div className='header'>
                Select Data to Synchronize
              </div>

              <div className='subheader'>
                Provide a SQL query to synchronize with the Activate Target
              </div>
            </div>

            <div className='d-flex'>
              <HdCoordinatedScrollAnchor elRef={coordinatedScrollAnchorRef.current}>
                <div
                  className={clsx(
                    'flex-1',
                    canShowObjectExplorer && styles.workbenchWithObjectExplorer
                  )}
                  ref={coordinatedScrollAnchorRef}>
                  <Workbench
                    destinationId={destination.id}
                    dataSource={dataSource}
                    dataSourceUIState={dataSourceUIState}
                    onFolderDelete={onSchemaDelete}
                    schemaExplorerUIOptions={SCHEMA_EXPLORER_ACTIVATION_UI_OPTIONS}
                    query={selectedQuery}
                    queryExecutionResponse={queryExecutionResponse}
                    onQueryChange={setSelectedQuery}
                    onQueryExecutionResponseChange={onQueryExecutionResponseChange}
                    getDataSource={getDataSource}
                    stopQueryExecution$={TransformModelAPI.stopQueryExecution.bind(TransformModelAPI)}
                    getQueryExecutionTaskId$={TransformModelAPI.getQueryExecutionTaskId.bind(TransformModelAPI)}
                    getQueryExecutionTaskResponse$={TransformModelAPI.getQueryExecutionTaskResponse.bind(TransformModelAPI)}
                    onRunQueryClick={onRunQueryClick}
                    runQueryShortcutCommand='activation.runQueryAndContinue'
                    queryEditorProps={{
                      QueryEditorFooterActions,
                      runQueryText: 'Test Query'
                    }}
                    duplicateColErrorMessage={DUPLICATE_COLUMN_ERROR_MESSAGE}
                  />
                </div>

                {canShowObjectExplorer ? (
                  <HdCoordinatedHeight elRef={objectExplorerHeightElem} fixedHeight>
                    <div
                      className={styles.objectExplorerWrapper}
                      ref={setObjectExplorerHeightElem}
                    >
                      <TargetObjectExplorer
                        className='ml-5 h-100'
                        target={target}
                        currentTargetObject={currentTargetObject}
                        targetObjectChanged={setCurrentTargetObject}
                      />
                    </div>
                  </HdCoordinatedHeight>
                ) : null}
              </HdCoordinatedScrollAnchor>
            </div>
          </div>
        </HdCoordinatedHeightAnchor>
      </HdCoordinatedScroll>
    </div>
  );
}
